import { Plus, Trash2 } from 'lucide-react';
import { TimeInput } from './TimeInput';
import {
  OPENING_HOUR_DAYS,
  type OpeningPeriod,
  type ShopOpeningHoursForm,
  type WeekDayKey,
} from '../utils/openingHours';

interface ShopOpeningHoursEditorProps {
  value: ShopOpeningHoursForm;
  onChange: (next: ShopOpeningHoursForm) => void;
}

const DEFAULT_FIRST_PERIOD: OpeningPeriod = { open: '10:00', close: '19:00' };
const DEFAULT_NEXT_PERIOD: OpeningPeriod = { open: '14:00', close: '18:00' };

export function ShopOpeningHoursEditor({ value, onChange }: ShopOpeningHoursEditorProps) {
  const updateDay = (day: WeekDayKey, periods: OpeningPeriod[] | undefined) => {
    const next: ShopOpeningHoursForm = { ...value };
    if (!periods || periods.length === 0) {
      delete next[day];
    } else {
      next[day] = periods;
    }
    onChange(next);
  };

  const addPeriod = (day: WeekDayKey) => {
    const current = value[day] || [];
    updateDay(day, [...current, current.length === 0 ? DEFAULT_FIRST_PERIOD : DEFAULT_NEXT_PERIOD]);
  };

  const updatePeriod = (day: WeekDayKey, index: number, field: keyof OpeningPeriod, nextValue: string) => {
    const current = value[day] || [];
    updateDay(
      day,
      current.map((period, periodIndex) => (
        periodIndex === index ? { ...period, [field]: nextValue } : period
      )),
    );
  };

  const removePeriod = (day: WeekDayKey, index: number) => {
    const current = value[day] || [];
    updateDay(day, current.filter((_, periodIndex) => periodIndex !== index));
  };

  return (
    <div className="space-y-4">
      {OPENING_HOUR_DAYS.map((day) => {
        const periods = value[day.key] || [];
        const isOpen = periods.length > 0;

        return (
          <div key={day.key} className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <div className="w-20 pt-1">
              <span className="text-sm font-medium text-textPrimary">{day.label}</span>
            </div>

            <div className="flex-1 space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isOpen}
                  onChange={(event) => {
                    if (event.target.checked) {
                      updateDay(day.key, [DEFAULT_FIRST_PERIOD]);
                    } else {
                      updateDay(day.key, undefined);
                    }
                  }}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-2"
                />
                <span className="text-sm text-gray-600">Ouvert</span>
              </label>

              {isOpen && (
                <div className="space-y-2">
                  {periods.map((period, index) => (
                    <div key={`${day.key}-${index}`} className="flex flex-wrap items-center gap-2">
                      <TimeInput
                        value={period.open}
                        onChange={(nextValue) => updatePeriod(day.key, index, 'open', nextValue)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      />
                      <span className="text-gray-500">à</span>
                      <TimeInput
                        value={period.close}
                        onChange={(nextValue) => updatePeriod(day.key, index, 'close', nextValue)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      />
                      {periods.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePeriod(day.key, index)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-white text-red-600 hover:bg-red-50"
                          title="Supprimer cette période"
                          aria-label={`Supprimer la période ${index + 1} du ${day.label.toLowerCase()}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addPeriod(day.key)}
                    className="inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50"
                  >
                    <Plus className="h-4 w-4" />
                    Ajouter une période
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
